// Remises de virements fournisseurs (B1b, 03/11/2026 ; répartition directe le 04/11/2026).
// La banque débite en une seule ligne de relevé un lot de virements ; le fichier CSV de
// la remise en donne le détail. Contrôles bloquants : somme des virements = total de la
// remise = ligne de débit du relevé. Chaque virement va au débit du compte de son
// fournisseur (fiche reconnue, ou créée à la confirmation) ; la facture certaine est
// rapprochée en plus, sinon « facture attendue ». Seul un virement qui correspond à
// plusieurs fiches demande un choix. Une confirmation par remise ; rien n'est
// enregistré avant.
import { useEffect, useState } from "react";
import api from "../services/api";
import { downloadProtectedFile } from "../utils/openBlob";
import InvoicePdfButton from "./InvoicePdfButton";

const toBase64 = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result).split(",")[1] || "");
  r.onerror = reject;
  r.readAsDataURL(file);
});
const eur = (n) => `${Number(n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const frDate = (d) => (d ? String(d).slice(0, 10).split("-").reverse().join("/") : "—");
const MODE = {
  ventilation: "ligne en attente : une écriture de banque répartit le débit entre les fournisseurs",
  reclassement: "ligne déjà validée sur le compte d'attente 471 : une écriture la reporte sur les fournisseurs",
};

function InvoiceCell({ invoice }) {
  if (!invoice) return <span style={{ color: "#6b7280" }}>facture attendue</span>;
  return <span style={{ color: "#16a34a" }}>Facture {invoice.supplier_ref || invoice.number} du {frDate(invoice.date)} rapprochée <InvoicePdfButton kind="achat" id={invoice.purchase_invoice_id} /></span>;
}

export default function BankRemisesModal({ onClose, onDone }) {
  const [payload, setPayload] = useState(null);
  const [preview, setPreview] = useState(null);
  const [choices, setChoices] = useState({}); // clé de remise → { n° de virement → compte 401 choisi }
  const [chosen, setChosen] = useState({});   // clé de remise → importer ?
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const choose = async (file) => {
    if (!file) return;
    setBusy(true); setError(""); setPreview(null); setResults(null);
    try {
      const data = { filename: file.name, content_base64: await toBase64(file) };
      const p = await api.bankImport.remisesPreview(data);
      setPayload(data); setPreview(p); setChoices({});
      setChosen(Object.fromEntries(p.remises.filter(r => r.ok).map(r => [r.key, true])));
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };
  const missing = (r) => r.transfers.filter(t => t.status === "a_choisir" && !choices[r.key]?.[t.line_no]).length;
  const selected = (preview?.remises || []).filter(r => r.ok && chosen[r.key]);
  const blockedByChoice = selected.some(r => missing(r) > 0);

  const confirm = async () => {
    if (!window.confirm(`Importer ${selected.length} remise(s) ? Chaque virement est passé au débit du compte de son fournisseur, à la date de la ligne du relevé ; les fiches à créer le sont maintenant. Une remise refusée n'empêche pas les autres.`)) return;
    setBusy(true); setError("");
    try {
      const r = await api.bankImport.remisesConfirm({ ...payload, choices: Object.fromEntries(selected.map(x => [x.key, choices[x.key] || {}])) });
      setResults(r);
      onDone && onDone(r);
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 1150, width: "96%" }}>
        <div className="modal-header"><h3 className="modal-title">🧾 Remises de virements fournisseurs</h3><button className="modal-close" onClick={onClose}>✕</button></div>
        <div style={{ fontSize: 12.5, color: "#374151", marginBottom: 10 }}>
          Une <strong>remise</strong> est un lot de virements que la banque débite en une seule ligne du relevé. Choisissez le fichier CSV de la remise (export de la banque, ou le{" "}
          <button className="btn sm" onClick={() => downloadProtectedFile(api.bankImport.remisesTemplate)}>⬇ modèle CSV</button>) :
          chaque virement est passé au débit du compte de son fournisseur, et relié à sa facture quand elle est certaine (même montant).
          La somme des virements doit être égale au total de la remise et à la ligne de débit du relevé, au centime. <strong>Rien n'est enregistré avant la confirmation.</strong>
        </div>
        <input type="file" accept=".csv,.txt" disabled={busy} onChange={e => choose(e.target.files?.[0])} />
        {busy && <span style={{ marginLeft: 8, fontSize: 12 }}>Lecture…</span>}
        {error && <div className="alert error" style={{ marginTop: 8 }}>{error}</div>}

        {preview && !results && (
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 12.5, marginBottom: 8 }}>
              {preview.ok} remise(s) juste(s){preview.blocked ? `, ${preview.blocked} bloquée(s) (motif sous chacune)` : ""}.
              {" "}<strong>Facture attendue</strong> : le paiement reste sur le compte du fournisseur jusqu'à l'import de sa facture (facture de l'exercice précédent : soldée par les soldes d'ouverture du FEC).
            </div>
            {preview.remises.map(r => (
              <div key={r.key} style={{ border: "1px solid #e5e7eb", borderRadius: 6, padding: 10, marginBottom: 10, background: r.ok ? "#fff" : "#fef2f2" }}>
                <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                  {r.ok && <input type="checkbox" checked={!!chosen[r.key]} onChange={e => setChosen(s => ({ ...s, [r.key]: e.target.checked }))} />}
                  <strong>Remise {r.ref || ""} du {frDate(r.exec_date)}</strong>
                  <span>{r.salary ? "remise de salaires (détail non affiché)" : `${r.transfers.length} virement(s) · total ${eur(r.total)}`}</span>
                  {r.statement && <span style={{ fontSize: 12, color: "#374151" }}>ligne du relevé du {frDate(r.statement.date)} ({eur(r.statement.amount)})</span>}
                  {r.ok ? <span style={{ color: "#16a34a", fontSize: 12 }}>✓ somme = total = relevé</span> : <span style={{ color: "#b91c1c", fontSize: 12 }}>⛔ bloquée</span>}
                </div>
                {!r.ok && <ul style={{ margin: "6px 0 0 18px", color: "#b91c1c", fontSize: 12.5 }}>{r.blockers.map((b, i) => <li key={i}>{b}</li>)}</ul>}
                {r.ok && <div style={{ fontSize: 12, color: "#6b7280", margin: "4px 0" }}>{MODE[r.statement.mode]}</div>}
                {r.ok && (
                  <table className="table" style={{ fontSize: 12.5, marginTop: 6 }}>
                    <thead><tr><th>Bénéficiaire</th><th style={{ textAlign: "right" }}>Montant</th><th>Fournisseur (compte débité)</th><th>Facture</th></tr></thead>
                    <tbody>
                      {r.transfers.map(t => {
                        const pick = choices[r.key]?.[t.line_no] || "";
                        const cand = t.status === "a_choisir" ? t.candidates.find(c => c.account === pick) : null;
                        return (
                          <tr key={t.line_no} style={t.status === "a_choisir" && !pick ? { background: "#fffbeb" } : undefined}>
                            <td>{t.beneficiary}{t.reference ? <div style={{ fontSize: 11, color: "#6b7280" }}>{t.reference}</div> : null}</td>
                            <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>{eur(t.amount)}</td>
                            <td>
                              {t.status === "a_choisir" ? (
                                <select value={pick} onChange={e => setChoices(s => ({ ...s, [r.key]: { ...s[r.key], [t.line_no]: e.target.value } }))} style={{ maxWidth: 330 }}>
                                  <option value="">— plusieurs fiches : à choisir —</option>
                                  {t.candidates.map(c => <option key={c.account} value={c.account}>{c.account} — {c.orphan ? "code sans fiche, fiche créée à la confirmation" : c.name}</option>)}
                                </select>
                              ) : (
                                <>{t.supplier.account} — {t.supplier.name}{t.supplier.create && <span style={{ color: "#b45309" }}>{t.supplier.orphan ? " · code déjà utilisé en comptabilité, sans fiche : fiche créée sur ce code à la confirmation" : " · fiche créée à la confirmation"}</span>}</>
                              )}
                            </td>
                            <td>{t.status === "a_choisir" ? (cand ? <InvoiceCell invoice={cand.invoice} /> : <span style={{ color: "#6b7280" }}>—</span>) : <InvoiceCell invoice={t.invoice} />}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
                {r.ok && missing(r) > 0 && <div style={{ fontSize: 12, color: "#b45309" }}>{missing(r)} virement(s) correspondent à plusieurs fiches : choisissez le fournisseur (ligne surlignée) avant la confirmation.</div>}
              </div>
            ))}
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button className="btn" onClick={onClose}>Fermer</button>
              <button className="btn primary" disabled={busy || !selected.length || blockedByChoice} onClick={confirm}>✓ Importer {selected.length} remise(s)</button>
            </div>
          </div>
        )}

        {results && (
          <div style={{ marginTop: 12 }}>
            {results.results.map(x => (
              <div key={x.key} style={{ fontSize: 13, marginBottom: 4, color: x.ok ? "#16a34a" : "#b91c1c" }}>
                {x.ok ? `✅ Remise ${x.key.includes("|") ? "" : x.key} importée : ${x.invoices} facture(s) rapprochée(s)${x.awaiting ? `, ${x.awaiting} virement(s) en attente de facture sur le compte du fournisseur (${eur(x.awaiting_amount)})` : ""}${x.created_suppliers?.length ? `, ${x.created_suppliers.length} fiche(s) fournisseur créée(s) (${x.created_suppliers.map(c => c.code).join(", ")})` : ""}.`
                  : `⛔ Remise ${x.key.includes("|") ? "" : x.key} refusée : ${x.error}`}
              </div>
            ))}
            <div style={{ textAlign: "right" }}><button className="btn" onClick={onClose}>Fermer</button></div>
          </div>
        )}
      </div>
    </div>
  );
}

/** Détail de la remise d'une ligne du relevé. */
export function BankRemiseDetail({ row, onClose }) {
  const [data, setData] = useState(undefined);
  const [error, setError] = useState("");
  useEffect(() => { api.bankImport.remiseOf(row.id).then(d => setData(d)).catch(e => setError(e.message)); }, [row.id]);
  // anterieur / attente : remises importées avant le 04/11/2026
  const TREAT = { fournisseur: "facture attendue", anterieur: "exercice antérieur", attente: "compte d'attente 471" };
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 900, width: "96%" }}>
        <div className="modal-header"><h3 className="modal-title">🧾 Remise du {frDate(data?.exec_date || row.date)}</h3><button className="modal-close" onClick={onClose}>✕</button></div>
        {error && <div className="alert error">{error}</div>}
        {data === undefined && !error && <div>Chargement…</div>}
        {data === null && <div>Aucune remise active sur cette ligne.</div>}
        {data && (
          <>
            <div style={{ fontSize: 12.5, marginBottom: 6 }}>{data.transfer_count} virement(s) · total {eur(data.total)} · {data.mode === "ventilation" ? "ventilation de la ligne" : "reclassement depuis 471"}. Pour défaire la remise : {data.mode === "ventilation" ? "« Annuler la validation »" : "« Annuler le rattachement »"} sur la ligne.</div>
            <table className="table" style={{ fontSize: 12.5 }}>
              <thead><tr><th>Bénéficiaire</th><th>Référence</th><th style={{ textAlign: "right" }}>Montant</th><th>Imputation</th></tr></thead>
              <tbody>
                {data.transfers.map(t => (
                  <tr key={t.line_no}><td>{t.beneficiary}</td><td>{t.reference || "—"}</td><td style={{ textAlign: "right" }}>{eur(t.amount)}</td>
                    <td>{t.treatment === "facture" ? `Facture ${t.invoice_number}${t.account_code ? ` (${t.account_code})` : ""}` : `${TREAT[t.treatment] || t.treatment} (${t.account_code})`}</td></tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
}
